import React from 'react';
import { ListProps, TextField } from 'react-admin';

import {
  TextInput, Grid, GridList, Forbidden, SelectArrayInput, SelectInput, DateField, PhoneField,
} from '@components';
import { rbac } from '@utils/rbac';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { CallStatusField } from './CallStatusField';
import { CallRiskField } from './CallRiskField';
import { CreditStatusField, creditStatusOptions } from '@pages/credit/CreditStatusField';
import { CreditCallListActions } from '@pages/credit-call/CreditCallListActions';

export const CreditCallList = (props: ListProps) => {
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const exporter = useExporter({
    filename: 'Tasks',
    props: {
      'credit.id': {
        label: 'Credit code',
        prop: 'credit.code',
      },
      'credit.status': {
        label: 'Credit status',
        prop: 'credit.statusEn',
      },
      'credit.user.fullName': {
        label: 'User Full name',
      },
      'credit.user.phone': {
        label: 'Phone number',
        transform: rbac.canShowUserPhone() ? 'phone' : 'masked',
      },
      'credit.userId': {
        label: 'User Full name',
        prop: 'credit.user.fullName',
      },
      'title': {
        label: 'Task title/type',
      },
      'responsibleAdmin.name': {
        label: 'Task manager',
        prop: 'responsibleAdmin.name',
      },
      'status': {
        label: 'Call status',
        prop: 'statusEn',
      },
      'risk': {
        label: 'Call risk',
        prop: 'riskEn',
      },
      'observation': {
        label: 'Observation/Call Log',
      },
      'contactBy': {
        label: 'Contact by',
        prop: 'contactBy',
        transform: 'date',
      },
      'contactAt': {
        label: 'Contact at',
        prop: 'contactAt',
        transform: 'date',
      },
      'credit.paymentAt': {
        label: 'Payment date',
        prop: 'credit.paymentAt',
        transform: 'date',
      },
      'createdAt': {
        label: 'Created at',
        transform: 'date',
      },
    },
  });

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList {...props}
      filters={Filters}
      sort={{ field: 'id', order: 'DESC' }}
      hideRemoveButton={!rbac.isSuperadmin()}
      exporter={exporter}
      editComponent={rbacEdit ? <CreditCallListActions /> : undefined}
    >
      <Grid
        rowClick="edit"
        defaultColumns={['id', 'credit.id', 'credit.code', 'credit.user.fullName', 'credit.user.phone',
          'title', 'responsibleAdmin.name', 'status', 'risk',
        ]}
      >
        <TextField source="credit.code" label="Credit code" />
        <CreditStatusField source="credit.status" label="Credit status" />
        <TextField source="credit.user.fullName" label="User Full name" />
        <PhoneField source="credit.user.phone" label="Phone number"
          isMasked={!rbac.canShowUserPhone()} />
        <TextField source="title" label="Task title/type" />
        <TextField source="responsibleAdmin.name" label="Task manager" />
        <CallStatusField source="status" label="Call status" />
        <CallRiskField source="risk" label="Call risk" />
        <TextField source="observation" label="Observation/Call Log" />
        <DateField source="contactBy" label="Contact by" />
        <DateField source="contactAt" label="Contact at" />
        <DateField source="credit.paymentAt" label="Payment date" />
        <DateField source="createdAt" label="Created at" />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" label="Search" alwaysOn style={{ width: 220 }} />,
  <SelectArrayInput
    style={{ width: 200 }}
    key="type"
    source="type"
    choices={[
      { id: 'welcome', name: 'Welcome call', color: '#4CAF50' },
      { id: 'follow_up', name: 'Follow Up', color: '#2196F3' },
      { id: 'collection', name: 'Collections', color: '#673AB7' },
      { id: 'final', name: 'Collections Final', color: '#e36e26' },
      { id: 'manual', name: 'Manual', color: '#888' },
    ]}
    className="MS-field"
    alwaysOn
  />,
  <SelectInput
    label="Call status"
    key="status"
    source="status"
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'complete', name: 'Complete' },
      { id: 'not_contacted', name: 'Not Contacted' },
    ]}
    alwaysOn
  />,
  <SelectInput
    label="Call Date"
    key="callDate"
    source="callDate"
    choices={[
      { id: 'overdue', name: 'Overdue Calls' },
      { id: 'future', name: 'Future Calls' },
    ]}
  />,
  <SelectArrayInput
    key="creditStatus"
    source="creditStatus"
    choices={creditStatusOptions}
    className="MS-field"
    style={{ width: 250 }}
    alwaysOn
  />,
];
